import type { GuestScopeRequest, GuestScopeView } from "@keydials/api-client";

/**
 * The pure logic behind the guest grant matrix (KEY-852). The panel shows sections, their action points and —
 * where the dial offers it — each action point's items, as a tree of checkboxes with a role on every row; this
 * file turns the ticks into the smallest set of scopes the API needs, and turns an existing grant's scopes back
 * into ticks and into one readable line.
 *
 * A selection is a set of "leaves", each with the role the guest holds there: an item; an action point that has
 * no items listed (the dial doesn't offer item-level access, or it has none); or a section that has no action
 * points. A node is fully ticked when all its leaves are. That keeps ticking a section, ticking its action points
 * or items one by one, and loading an existing grant all the same operation — and a role set on a row is just
 * that role written onto every leaf beneath it.
 */

export type GuestItem = { key: string; name: string };
export type GuestSegment = { key: string; name: string; items?: GuestItem[] };
export type GuestSection = { key: string; name: string; segments: GuestSegment[] };

/** The leaves ticked, each with its role. `wholeRole` is the role when the whole dial is chosen. */
export type GuestSelection = { whole: boolean; wholeRole: string; leaves: ReadonlyMap<string, string> };

export type CheckState = "all" | "some" | "none";

/** A row's role: one role, several different ones beneath it, or nothing ticked. */
export type RowRole = string | "mixed" | null;

const SEP = "\u0000";

/** The leaf id for an item, an action point, or a section that has no action points of its own. */
export function leafKey(sectionKey: string, segmentKey?: string | null, itemKey?: string | null): string {
  if (!segmentKey) return sectionKey;
  return itemKey ? `${sectionKey}${SEP}${segmentKey}${SEP}${itemKey}` : `${sectionKey}${SEP}${segmentKey}`;
}

export function segmentLeaves(sectionKey: string, segment: GuestSegment): string[] {
  return segment.items && segment.items.length > 0
    ? segment.items.map((i) => leafKey(sectionKey, segment.key, i.key))
    : [leafKey(sectionKey, segment.key)];
}

export function sectionLeaves(section: GuestSection): string[] {
  return section.segments.length === 0
    ? [leafKey(section.key)]
    : section.segments.flatMap((sg) => segmentLeaves(section.key, sg));
}

export const wholeDialSelection: GuestSelection = { whole: true, wholeRole: "Reader", leaves: new Map() };

export function emptySelection(role = "Reader"): GuestSelection {
  return { whole: false, wholeRole: role, leaves: new Map() };
}

function stateOf(leaves: string[], sel: GuestSelection): CheckState {
  const ticked = leaves.filter((l) => sel.leaves.has(l)).length;
  if (ticked === 0) return "none";
  return ticked === leaves.length ? "all" : "some";
}

export const sectionCheckState = (section: GuestSection, sel: GuestSelection): CheckState =>
  stateOf(sectionLeaves(section), sel);

export const segmentCheckState = (sectionKey: string, segment: GuestSegment, sel: GuestSelection): CheckState =>
  stateOf(segmentLeaves(sectionKey, segment), sel);

/** The role a row shows: the one role its ticked leaves share, "mixed" when they differ, null when none is ticked. */
export function rowRole(leaves: string[], sel: GuestSelection): RowRole {
  const roles = new Set(leaves.filter((l) => sel.leaves.has(l)).map((l) => sel.leaves.get(l)!));
  if (roles.size === 0) return null;
  return roles.size === 1 ? [...roles][0] : "mixed";
}

/** Ticking a row covers every leaf beneath it at `role`; clicking a fully ticked row clears it, a partial one fills it
 * (leaves already ticked keep their own role). */
export function toggleLeaves(leaves: string[], sel: GuestSelection, role: string): GuestSelection {
  const next = new Map(sel.leaves);
  const fill = stateOf(leaves, sel) !== "all";
  for (const l of leaves) {
    if (!fill) next.delete(l);
    else if (!next.has(l)) next.set(l, role);
  }
  return { ...sel, whole: false, leaves: next };
}

export const toggleSection = (section: GuestSection, sel: GuestSelection, role: string): GuestSelection =>
  toggleLeaves(sectionLeaves(section), sel, role);

export const toggleSegment = (sectionKey: string, segment: GuestSegment, sel: GuestSelection, role: string): GuestSelection =>
  toggleLeaves(segmentLeaves(sectionKey, segment), sel, role);

export const toggleItem = (sectionKey: string, segmentKey: string, itemKey: string, sel: GuestSelection, role: string): GuestSelection =>
  toggleLeaves([leafKey(sectionKey, segmentKey, itemKey)], sel, role);

/** Sets the role on every TICKED leaf beneath a row — a row's role control never ticks anything by itself. */
export function setRowRole(leaves: string[], sel: GuestSelection, role: string): GuestSelection {
  const next = new Map(sel.leaves);
  for (const l of leaves) if (next.has(l)) next.set(l, role);
  return { ...sel, leaves: next };
}

export function setWhole(whole: boolean, sel: GuestSelection): GuestSelection {
  return { ...sel, whole };
}

export function setWholeRole(role: string, sel: GuestSelection): GuestSelection {
  return { ...sel, wholeRole: role };
}

/** Whether there is anything to invite for: the whole dial, or at least one ticked leaf that still exists. */
export function hasAnyScope(sections: GuestSection[], sel: GuestSelection): boolean {
  return sel.whole || sections.some((s) => sectionCheckState(s, sel) !== "none");
}

/**
 * The smallest scope set for the selection, each scope with its role. Whole dial is `null` (the API reads no
 * scopes as the whole instance). A fully ticked section whose leaves share one role is one section scope; a fully
 * ticked action point likewise; only what is left lists items.
 */
export function minimiseScopes(sections: GuestSection[], sel: GuestSelection): GuestScopeRequest[] | null {
  if (sel.whole) return null;
  const scopes: GuestScopeRequest[] = [];
  for (const section of sections) {
    const leaves = sectionLeaves(section);
    const role = rowRole(leaves, sel);
    if (stateOf(leaves, sel) === "none") continue;
    if (stateOf(leaves, sel) === "all" && role !== "mixed" && role !== null) {
      scopes.push({ sectionKey: section.key, segmentKey: null, role });
      continue;
    }
    for (const sg of section.segments) {
      const sgLeaves = segmentLeaves(section.key, sg);
      const sgRole = rowRole(sgLeaves, sel);
      const state = stateOf(sgLeaves, sel);
      if (state === "none") continue;
      if (state === "all" && sgRole !== "mixed" && sgRole !== null) {
        scopes.push({ sectionKey: section.key, segmentKey: sg.key, role: sgRole });
        continue;
      }
      for (const item of sg.items ?? []) {
        const r = sel.leaves.get(leafKey(section.key, sg.key, item.key));
        if (r) scopes.push({ sectionKey: section.key, segmentKey: sg.key, itemKey: item.key, role: r });
      }
    }
  }
  return scopes;
}

/** The ticks an existing grant's scopes stand for. Scopes naming something that no longer exists are dropped. */
export function selectionFromScopes(
  sections: GuestSection[],
  scopes: Pick<GuestScopeView, "sectionKey" | "segmentKey" | "itemKey" | "role">[],
): GuestSelection {
  const whole = scopes.length === 0 ? undefined : scopes.find((s) => !s.sectionKey);
  if (scopes.length === 0 || whole) return { whole: true, wholeRole: whole?.role ?? "Reader", leaves: new Map() };
  const leaves = new Map<string, string>();
  for (const s of scopes) {
    const section = sections.find((x) => x.key === s.sectionKey);
    if (!section) continue;
    const segment = s.segmentKey ? section.segments.find((sg) => sg.key === s.segmentKey) : undefined;
    const covered = !s.segmentKey
      ? sectionLeaves(section)
      : !segment
        ? []
        : s.itemKey
          ? (segment.items ?? []).some((i) => i.key === s.itemKey) ? [leafKey(section.key, segment.key, s.itemKey)] : []
          : segmentLeaves(section.key, segment);
    for (const l of covered) leaves.set(l, s.role);
  }
  return { whole: false, wholeRole: "Reader", leaves };
}

/** "Health › Appointments (Reader), School (Editor), Health › Medication › Inhaler (Editor)" — every scope of a
 * grant, each with its role. */
export function describeScopes(scopes: GuestScopeView[], sections: GuestSection[]): string {
  if (scopes.length === 0) return "Whole dial";
  return scopes
    .map((s) => {
      const role = s.role ? ` (${s.role})` : "";
      if (!s.sectionKey) return `Whole dial${role}`;
      const section = sections.find((x) => x.key === s.sectionKey);
      const name = section?.name ?? s.sectionKey;
      if (!s.segmentKey) return `${name}${role}`;
      const segment = section?.segments.find((sg) => sg.key === s.segmentKey);
      const path = `${name} › ${segment?.name ?? s.segmentKey}`;
      if (!s.itemKey) return `${path}${role}`;
      return `${path} › ${segment?.items?.find((i) => i.key === s.itemKey)?.name ?? s.itemKey}${role}`;
    })
    .join(", ");
}
